/**
 * emailSpec — Gmail + Outlook mail.
 *
 * v1 source-of-truth:
 *   ando-frontend/app/pages/OutlookPage.tsx (248)
 *   (Gmail page lives in the broader Email context — v1 hasn't isolated a
 *   dedicated page; the BE already speaks "email" as a capability slug.)
 */

import type { BaseConnection, CapabilityWizardSpec } from "@ando/ui";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Mail, Search, Shield, Zap } from "lucide-react";

import { createCapabilityApi } from "../api/capabilityApi";
import {
  ArubaIcon,
  GmailIcon,
  OutlookIcon,
  vendorBrandColor,
  vendorIcon,
} from "../components/vendorIcons";

export interface EmailConnection extends BaseConnection {
  readonly provider?: string; // gmail | outlook | aruba_mail
  readonly account_email?: string;
  readonly threads_synced?: number;
}

const iconCls = "h-4 w-4";

export const emailSpec: CapabilityWizardSpec<EmailConnection> = {
  capability: "email",
  title: "Email",
  subtitle: msg`Thread, allegati e CC indicizzati per ricerca naturale`,
  icon: <Mail className="h-5 w-5" aria-hidden />,
  gdprDeleteWindowMinutes: 30,

  vendors: [
    { id: "gmail", label: "Gmail", icon: <GmailIcon className={iconCls} /> },
    { id: "outlook", label: "Outlook", icon: <OutlookIcon className={iconCls} /> },
    // Aruba Mail — a standard IMAP/SMTP mailbox is "just email", so it lives
    // under the SAME connector; picking it in AddAccountMenu opens the
    // credentials step below (email + app-password) instead of OAuth.
    { id: "aruba_mail", label: "Aruba", icon: <ArubaIcon className={iconCls} /> },
  ],

  // ``vendor_picker`` retired May 24 2026 — picker is now AddAccountMenu.
  // OAuth vendors (Gmail/Outlook) use the unscoped ``authorize`` step; the
  // credentials-based Aruba vendor uses the ``vendor``-scoped step (the dialog
  // picks the matching step via ``findCredentialsStep(vendorId)``).
  steps: [
    {
      id: "authorize",
      kind: "oauth",
      title: msg`Autorizza`,
      description: msg`Ando legge solo gli ultimi 12 mesi per default; configurabile.`,
      oauth: { buttonLabel: msg`Autorizza email` },
    },
    {
      id: "aruba-creds",
      kind: "credentials",
      vendor: "aruba_mail",
      title: msg`Credenziali Aruba`,
      description: msg`Genera una app password dal pannello cliente Aruba e usala qui — Ando non vede mai la tua password principale.`,
      fields: [
        {
          name: "email_address",
          label: msg`Indirizzo email`,
          type: "email",
          required: true,
          placeholder: "mario.rossi@tuodominio.it",
        },
        {
          name: "password",
          label: msg`App password`,
          type: "password",
          required: true,
        },
      ],
    },
  ],

  benefits: [
    {
      icon: <Search className={iconCls} aria-hidden />,
      title: msg`Ricerca semantica`,
      body: msg`"Email di Mario su Q2" — risultati ordinati per rilevanza, non per data.`,
    },
    {
      icon: <Zap className={iconCls} aria-hidden />,
      title: msg`Riepilogo thread`,
      body: msg`Lunghe catene → summary 3-bullet + action items pronti.`,
    },
    {
      icon: <Mail className={iconCls} aria-hidden />,
      title: msg`Citazioni`,
      body: msg`Ogni risposta linka all'email originale.`,
    },
    {
      icon: <Shield className={iconCls} aria-hidden />,
      title: msg`Mai inviato senza consenso`,
      body: msg`Bozze proposte, mai inviate in autonomia.`,
    },
  ],

  api: createCapabilityApi<EmailConnection>({ capability: "email" }),
  connectionKeyField: "id" as keyof EmailConnection,
  // BE projects the email address into `connection_key` (see
  // `unified_connection_service.list_connections` — `connection_key` is the
  // unique key per type+provider, which is the mailbox address for email).
  // Fall through metadata too in case a future vendor stores it there.
  getDisplayName: (c) =>
    c.account_email ??
    c.connection_key ??
    (c.metadata as { email?: string } | undefined)?.email ??
    c.id ??
    i18nSingleton._(msg`Account`),
  getSecondaryId: (c) =>
    c.provider === "gmail"
      ? "Gmail"
      : c.provider === "outlook"
        ? "Outlook"
        : c.provider === "aruba_mail"
          ? "Aruba Mail"
          : (c.provider ?? null),
  getDisplayIcon: (c) => vendorIcon(c.provider ?? c.vendor, iconCls),
  getBrandColor: (c) => vendorBrandColor(c.provider ?? c.vendor),
};
